/**
 * Screenshot-Aufnahme per Screen-Capture-API (F4, PRD-Entscheidung 5).
 *
 * PERSISTENTE Tab-Verbindung: Der Melder gibt den Tab EINMAL frei; danach
 * bleibt der MediaStream offen und jeder weitere Screenshot ist ein
 * sofortiger Frame-Abgriff — ohne erneuten Dialog.
 *
 * Zwei Erfassungsquellen (Adapter-Schnitt, SCHNITTLISTE §5):
 *  - `SameOriginCapture` (web): Die Kundenanwendung läuft im same-origin-
 *    iframe → `preferCurrentTab` (eigener Tab) + Zuschnitt auf den
 *    iframe-Bereich (`frameAbgreifen(bereich)`).
 *  - `NinoxTabCapture` (ninox): Ninox läuft im Fremd-Tab (CSP verhindert
 *    Einbettung) → der Melder wählt den Ninox-Tab im Freigabe-Dialog; ein
 *    geteilter Hintergrund-Tab wird weiter erfasst (Live-Vorschau möglich).
 */

/**
 * Laengste Kante des Uploads: Retina-/5K-Aufnahmen erzeugen sonst PNGs
 * ueber dem Upload-Limit (Backend 10 MB, nginx 12 MB) — Chrome meldet das
 * bei HTTP/2 nur als "Network Error". 2560px reicht fuer Review + KI.
 */
const MAX_KANTE = 2560

function zielGroesse(breite: number, hoehe: number): [number, number] {
  const laengste = Math.max(breite, hoehe)
  if (laengste <= MAX_KANTE) return [breite, hoehe]
  const faktor = MAX_KANTE / laengste
  return [Math.round(breite * faktor), Math.round(hoehe * faktor)]
}

/**
 * Gemeinsame Basis beider Erfassungsquellen: Stream-/Track-/Video-Handling,
 * Zielgrößen-Begrenzung und Frame-Abgriff. Einmal `verbinden()` (nativer
 * Freigabe-Dialog), dann beliebig oft Frames abgreifen.
 */
/** Minimaler Typ für Chromiums CaptureController (Fokus-Verhalten nach der Freigabe). */
interface CaptureControllerLike {
  setFocusBehavior?: (verhalten: "focus-captured-surface" | "no-focus-change") => void
}

export abstract class TabCaptureBase {
  private stream: MediaStream | null = null
  private track: MediaStreamTrack | null = null
  private video: HTMLVideoElement | null = null
  /** Wird gerufen, wenn der Nutzer das Teilen beendet / den Tab schließt. */
  onEnded: (() => void) | null = null

  /** Chromium: `preferCurrentTab` (web/iframe) vs. freie Tab-Wahl (ninox). */
  protected abstract get eigenerTab(): boolean
  /** Fehlertext, wenn ohne Verbindung ein Frame angefordert wird. */
  protected abstract get nichtVerbundenText(): string

  get verbunden(): boolean {
    return this.track?.readyState === "live"
  }

  /** Der Live-Stream — zum Anzeigen einer Vorschau in einem <video>. */
  get mediaStream(): MediaStream | null {
    return this.stream
  }

  /** Einmalige Tab-Freigabe; hält den Stream danach offen. */
  async verbinden(): Promise<void> {
    // Chromium: Nach der Tab-Wahl NICHT in den geteilten Tab springen — der
    // Melder bleibt im Testerwerk (Hans 28.09., „kein Hin und Her").
    // CaptureController fehlt in lib.dom.d.ts und in Firefox/Safari.
    const Controller = (window as unknown as { CaptureController?: new () => CaptureControllerLike })
      .CaptureController
    const controller = Controller ? new Controller() : null
    controller?.setFocusBehavior?.("no-focus-change")

    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: { displaySurface: "browser" },
      audio: false,
      // Chromium: eigenen Tab vorauswaehlen (web/iframe) — beim Fremd-Tab den
      // Testerwerk-Tab aus der Auswahl nehmen (der ist es nie) und den
      // Tab-Wechsel über die Teilen-Leiste erlauben (kein neuer Dialog).
      ...(this.eigenerTab
        ? { preferCurrentTab: true }
        : { selfBrowserSurface: "exclude", surfaceSwitching: "include", monitorTypeSurfaces: "exclude" }),
      ...(controller ? { controller } : {}),
    } as DisplayMediaStreamOptions)

    const track = stream.getVideoTracks()[0]
    const video = document.createElement("video")
    video.srcObject = stream
    video.muted = true
    await video.play()
    if (!video.videoWidth) {
      await new Promise<void>((r) => {
        video.onloadedmetadata = () => r()
      })
    }

    track.addEventListener("ended", () => {
      this.aufraeumen()
      this.onEnded?.()
    })

    this.stream = stream
    this.track = track
    this.video = video
  }

  /**
   * Sofortiger Einzelbild-Abgriff aus dem laufenden Stream (kein Dialog).
   * Mit `bereich` (z. B. iframe-Rechteck) wird nur dieser Viewport-Ausschnitt
   * übernommen — die Tester-UI drumherum bleibt draußen. Umrechnung
   * Viewport → Video über das Größenverhältnis (deckt auch devicePixelRatio
   * ab). Teilt der Nutzer statt des Tabs etwas anderes (Fenster/Bildschirm),
   * passt der Ausschnitt ggf. nicht — dann hilft das Zuschneiden-Werkzeug
   * im Editor.
   */
  protected async frameAbgreifenIntern(bereich?: DOMRect | null): Promise<Blob> {
    if (!this.verbunden || !this.video) {
      throw new Error(this.nichtVerbundenText)
    }
    const video = this.video
    // Einen Frame abwarten, damit das Bild aktuell ist
    await new Promise((r) => requestAnimationFrame(r))
    const videoBreite = video.videoWidth || this.track?.getSettings().width || 1280
    const videoHoehe = video.videoHeight || this.track?.getSettings().height || 720

    // Quell-Ausschnitt im Video (Standard: alles)
    let sx = 0
    let sy = 0
    let sw = videoBreite
    let sh = videoHoehe
    if (bereich && bereich.width > 0 && bereich.height > 0) {
      const skalaX = videoBreite / window.innerWidth
      const skalaY = videoHoehe / window.innerHeight
      sx = Math.max(0, bereich.left * skalaX)
      sy = Math.max(0, bereich.top * skalaY)
      sw = Math.min(videoBreite - sx, bereich.width * skalaX)
      sh = Math.min(videoHoehe - sy, bereich.height * skalaY)
      if (sw < 50 || sh < 50) {
        // Ausschnitt unplausibel (z. B. anderes Fenster geteilt) → ganzes Bild
        sx = 0
        sy = 0
        sw = videoBreite
        sh = videoHoehe
      }
    }

    const canvas = document.createElement("canvas")
    ;[canvas.width, canvas.height] = zielGroesse(sw, sh)
    const ctx = canvas.getContext("2d")
    if (!ctx) throw new Error("Canvas-Kontext nicht verfügbar")
    ctx.drawImage(video, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height)

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("Screenshot fehlgeschlagen"))),
        "image/png"
      )
    })
  }

  /** Verbindung beenden (Stream stoppen). */
  trennen(): void {
    this.aufraeumen()
  }

  private aufraeumen(): void {
    this.stream?.getTracks().forEach((t) => t.stop())
    this.stream = null
    this.track = null
    this.video = null
  }
}

/**
 * Erfassungsquelle „web": Die Kundenanwendung läuft im same-origin-iframe —
 * eigener Tab (`preferCurrentTab`) + Zuschnitt auf den iframe-Bereich.
 */
export class SameOriginCapture extends TabCaptureBase {
  protected get eigenerTab(): boolean {
    return true
  }

  protected get nichtVerbundenText(): string {
    return "Tab ist nicht verbunden"
  }

  /** Frame-Abgriff, optional zugeschnitten auf den iframe-Bereich. */
  frameAbgreifen(bereich?: DOMRect | null): Promise<Blob> {
    return this.frameAbgreifenIntern(bereich)
  }
}

/**
 * Fremd-Tab (web-tab UND ninox): Die Anwendung läuft in einem eigenen
 * Browser-Tab — der Melder wählt ihn im Freigabe-Dialog; das ganze Tab-Bild
 * wird übernommen (kein Zuschnitt). Der Tester teilt mit der Anwendung
 * weder Origin noch Speicher (Issue #61).
 */
export class FremdTabCapture extends TabCaptureBase {
  protected get eigenerTab(): boolean {
    return false
  }

  protected get nichtVerbundenText(): string {
    return "Anwendungs-Tab ist nicht verbunden"
  }

  /** Frame-Abgriff des kompletten geteilten Tabs. */
  frameAbgreifen(): Promise<Blob> {
    return this.frameAbgreifenIntern()
  }
}

/** Erfassungsquelle „ninox": Spezialfall des Fremd-Tabs (eigener Fehlertext). */
export class NinoxTabCapture extends FremdTabCapture {
  protected get nichtVerbundenText(): string {
    return "Ninox-Tab ist nicht verbunden"
  }
}

/**
 * Sitzungsweite Capture-Instanzen (Hans 28.09.): Die Tab-Verbindung überlebt
 * das Verlassen der Melden-Seite — die zweite Meldung braucht keinen
 * Freigabe-Dialog mehr. Schlüssel = Anwendung; getrennt wird nur ausdrücklich
 * („Verbindung trennen") oder wenn der Browser das Teilen beendet.
 */
const sitzungsCaptures = new Map<string, FremdTabCapture>()

export function sitzungsCapture(schluessel: string, erzeugen: () => FremdTabCapture): FremdTabCapture {
  let capture = sitzungsCaptures.get(schluessel)
  if (!capture) {
    capture = erzeugen()
    sitzungsCaptures.set(schluessel, capture)
  }
  return capture
}

/** Bestehende Sitzungs-Verbindung nachschlagen, ohne eine anzulegen (Detailansicht:
 * „Vom verbundenen Tab" nur anbieten, wenn wirklich verbunden). */
export function sitzungsCaptureFalls(schluessel: string): FremdTabCapture | null {
  const capture = sitzungsCaptures.get(schluessel)
  return capture && capture.verbunden ? capture : null
}

/**
 * Hans 28.09.2026: Tab-Verbindung auch aus der Meldung heraus herstellen —
 * die Sitzungs-Verbindung lebt nur im Speicher der Seite; wer eine Meldung
 * direkt öffnet (neuer Tab, Neuladen), hat keine und soll trotzdem mit einem
 * Klick aufnehmen können. Freigabe-Dialog nur, wenn noch nicht verbunden.
 * Abbruch im Dialog wirft NotAllowedError — kein Fehler für den Anwender.
 */
export async function tabVerbindenUndAufnehmen(schluessel: string, ninox: boolean): Promise<Blob> {
  const capture = sitzungsCapture(schluessel, () =>
    ninox ? new NinoxTabCapture() : new FremdTabCapture()
  )
  if (!capture.verbunden) await capture.verbinden()
  return capture.frameAbgreifen()
}

/** Kompatibilitäts-Alias: bisheriger Original-Exportname (web-Quelle). */
export { SameOriginCapture as TabCapture }

/** Fallback (PRD §8 „Einbettung"): Screenshot als Datei hochladen —
 * ebenfalls auf MAX_KANTE begrenzen, kleine Bilder bleiben unveraendert. */
export async function dateiAlsBlob(file: File): Promise<Blob> {
  const bitmap = await createImageBitmap(file).catch(() => null)
  if (!bitmap) return file
  const [breite, hoehe] = zielGroesse(bitmap.width, bitmap.height)
  if (breite === bitmap.width && hoehe === bitmap.height) {
    bitmap.close()
    return file
  }
  const canvas = document.createElement("canvas")
  canvas.width = breite
  canvas.height = hoehe
  canvas.getContext("2d")!.drawImage(bitmap, 0, 0, breite, hoehe)
  bitmap.close()
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Bild nicht lesbar"))),
      "image/png"
    )
  })
}
